import { useMemo } from 'react';
import { CategoryTabs } from './CategoryTabs';
import { CategoryTabsSkeleton } from './CategoryTabsSkeleton';
import { usePredictionMarketCategories } from '@/lib/endpoints';
import { useAppSearchParams } from '@/hooks/useAppSearchParams';
import { useHiddenCategories } from '@/lib/territory/useTerritory';
import type { CategoryResponseDto } from '@/lib/models';

export interface CategoryTabsContainerProps {
  className?: string;
  /**
   * Compact mode used when the home top bar is pinned to the viewport.
   * Forwarded to {@link CategoryTabs} so the tab row scales down with
   * the pinned header.
   */
  compact?: boolean;
}

/**
 * Data-fetching wrapper for {@link CategoryTabs}. Owns the active-category
 * state (synced to the URL via `?category=`) and renders a skeleton while
 * loading. Sub-category selection lives in the page body — see
 * {@link SubCategoryBar}.
 */
export function CategoryTabsContainer({ className, compact = false }: CategoryTabsContainerProps) {
  const { categoryCode: activeCategory, setCategory } = useAppSearchParams();

  const { data, isLoading } = usePredictionMarketCategories();

  // Categories blocked for the user's territory (country ∪ state). Hidden here so
  // they never render; the server also rejects orders on them (category enforcer).
  const hiddenCategories = useHiddenCategories();
  const visible = useMemo(
    () => (data ?? []).filter((cat) => !hiddenCategories.includes(cat.code.toUpperCase())),
    [data, hiddenCategories],
  );

  const categories: CategoryResponseDto[] = [
    { id: 'trending', displayName: 'Trending', code: 'TRENDING', subCategories: [] },
    ...visible,
  ];

  if (isLoading) return <CategoryTabsSkeleton className={className} />;
  if (!data || data.length === 0) return null;

  return (
    <div className="w-full">
      <CategoryTabs
        categories={categories}
        activeCategory={activeCategory}
        onCategoryChange={setCategory}
        className={className}
        compact={compact}
      />
    </div>
  );
}
